import type { ValidationState } from '@/helpers/form'
import { GoogleAuth } from '@/services/capacitor.googleAuthPlugin'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { platform } from '@/services/platform'

import { afterAuth } from './logic'
import type { SocialAuthView } from './social'
import type { OnAuthStart, OnAuthSuccess, OnAuthError } from './models'

/*
 * Handle Google authentication API response in the native app.
 *
 * See https://github.com/CodetrixStudio/CapacitorGoogleAuth
 */
export class GoogleNativeAuthView implements SocialAuthView {
  public validation: ValidationState

  public onStart: OnAuthStart
  public onSuccess: OnAuthSuccess
  public onError: OnAuthError

  isEnabled: boolean = true

  constructor(
    validation: ValidationState,
    onStart: OnAuthStart,
    onSuccess: OnAuthSuccess,
    onError: OnAuthError,
  ) {
    this.validation = validation
    this.onStart = onStart
    this.onSuccess = onSuccess
    this.onError = onError
  }

  /**
   * Intialize native GoogleAuth plugin.
   */
  public init(): void {}

  /**
   * Login with Google.
   */
  async start(): Promise<void> {
    try {
      await this.onStart()
      const response = await GoogleAuth.signIn()
      const isIOS = await platform.isNativeIOSApp()

      const googleToken = response.authentication.idToken
      const model = { googleToken: googleToken }

      const authData = await backend.googleLoginOrRegister(model, isIOS)

      authData.user.full_name = response.givenName

      // Redirect the user to the app
      const nextPage = await afterAuth(authData, 'google')

      return this.onSuccess(nextPage)
    } catch (error) {
      const message = 'Google Authentication error'
      this.validation.showMessage('social', message)
      Sentry.withScope((scope) => {
        scope.setExtra('Google Native Signin Error', error)
        Sentry.captureException(error)
      })
      this.onError()
    }
  }

  static async tryLogout(): Promise<void> {
    try {
      await GoogleAuth.signOut()
    } catch (error) {
      // We do not need this in Sentry, just log to console, so
      // it may appear in Sentry data if another error happens
      // after that.
      console.log('Error on native Google logout', error)
    }
  }
}
